import React from 'react';
import { Building2, Target, Users, TrendingUp } from 'lucide-react';

const AboutSection = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">About Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Your Trusted Partner in Trademark Protection
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              With over 15 years of experience in intellectual property law, we specialize exclusively in trademark registration services, helping businesses protect their most valuable asset - their brand identity.
            </p>
          </div>

          {/* Company Image and Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/5673490/pexels-photo-5673490.jpeg"
                alt="Professional legal team consultation"
                className="rounded-lg shadow-xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-secondary text-white p-6 rounded-lg shadow-xl">
                <div className="text-3xl font-bold">15+</div>
                <div className="text-sm">Years of Excellence</div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-primary mb-6">
                Why Businesses Choose Us for Trademark Registration
              </h3>
              <p className="text-gray-700 mb-4 leading-relaxed">
                We understand that your brand is more than just a name or logo—it's your business identity, your reputation, and your competitive advantage. Our dedicated team of trademark specialists works tirelessly to ensure your brand receives comprehensive legal protection through the United States Patent and Trademark Office (USPTO).
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Unlike general legal firms, we focus exclusively on trademark registration, allowing us to provide specialized expertise, streamlined processes, and higher success rates. Our proven track record speaks for itself with over 1000 trademarks successfully registered and a 95% approval rate.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Every trademark application we handle receives personalized attention from experienced attorneys who understand the nuances of trademark law, USPTO examination procedures, and brand protection strategies. We don't just file paperwork—we develop comprehensive protection strategies that safeguard your brand assets and support your long-term business growth.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our commitment extends beyond initial registration. We provide ongoing support for trademark maintenance, renewal filings, monitoring services, and enforcement actions. When you choose us, you're not just getting a service provider—you're gaining a long-term partner dedicated to protecting one of your most valuable business assets.
              </p>
              
              {/* Key Highlights */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-accent p-4 rounded-lg">
                  <div className="text-secondary text-2xl font-bold">1000+</div>
                  <div className="text-sm text-gray-600">Trademarks Registered</div>
                </div>
                <div className="bg-accent p-4 rounded-lg">
                  <div className="text-secondary text-2xl font-bold">95%</div>
                  <div className="text-sm text-gray-600">Success Rate</div>
                </div>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border-2 border-accent p-6 rounded-lg hover:border-secondary transition-all hover:shadow-lg">
              <div className="bg-accent w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Target className="text-secondary" size={24} />
              </div>
              <h4 className="text-lg font-semibold text-primary mb-2">Specialized Focus</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                We exclusively handle trademark registrations, ensuring unmatched expertise and attention to detail.
              </p>
            </div>

            <div className="bg-white border-2 border-accent p-6 rounded-lg hover:border-secondary transition-all hover:shadow-lg">
              <div className="bg-accent w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Building2 className="text-secondary" size={24} />
              </div>
              <h4 className="text-lg font-semibold text-primary mb-2">USPTO Experts</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Deep understanding of USPTO procedures and requirements for smooth filing processes.
              </p>
            </div>

            <div className="bg-white border-2 border-accent p-6 rounded-lg hover:border-secondary transition-all hover:shadow-lg">
              <div className="bg-accent w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <Users className="text-secondary" size={24} />
              </div>
              <h4 className="text-lg font-semibold text-primary mb-2">Client-Centric</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Personalized service with dedicated case managers guiding you through every step.
              </p>
            </div>

            <div className="bg-white border-2 border-accent p-6 rounded-lg hover:border-secondary transition-all hover:shadow-lg">
              <div className="bg-accent w-12 h-12 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="text-secondary" size={24} />
              </div>
              <h4 className="text-lg font-semibold text-primary mb-2">Proven Results</h4>
              <p className="text-gray-600 text-sm leading-relaxed">
                Track record of successful registrations across diverse industries and business sizes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
